import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { TIERS, TIER_COLOR, TIER_META } from '../lib/tiers.js';
import { useT } from '../lib/i18n.js';
import { useDevice } from '../lib/device.js';
import { FilterChips, type Chip } from '../components/FilterChips.js';
import { ThumbButtons } from '../components/ThumbButtons.js';
import { DiscussStanceModal } from '../components/DiscussStanceModal.js';

interface Analysis {
  id: string;
  createdAt: number;
  source: string;
  context: string | null;
  question: string;
  statement: string;
  predicted: boolean | null;
  confidence: number | null;
  reasoning: string | null;
  comment: string | null;
  verdict: boolean | null;
  aligned: boolean | null;
  visibility: string | null;
  memoryId?: string | null;
  mlVisibility?: { tier: string; confidence: number } | null;
}


interface Budget {
  caps: { questionCap: number; tokenBudget: number };
  usage: { questions: number; tokens: number };
  questionsLeft: number;
  exhausted: boolean;
}
interface Stats {
  total: number; verified: number; pending: number; needsAlignment: number; sharedPending: number; threshold: number;
  alignedCount: number; alignmentRate: number | null; confidentAccuracy: number | null; budget: Budget;
}

// Status = a single-select segment mapped to the server `filter=` query (the
// server owns the calibrated needs_alignment classification). Tier + source are
// combinable, client-side chips (the Coffre's filters, ported here as asked).
type Status = 'needs' | 'pending' | 'verified' | 'all';
const STATUS_PARAM: Record<Status, string> = { needs: 'needs_alignment', pending: 'pending', verified: 'verified', all: 'all' };
const SOURCES = ['news', 'conversation', 'memory'] as const;
const SOURCE_LABEL: Record<string, string> = { news: 'actualité', conversation: 'conversation', memory: 'souvenir' };

export function Align() {
  const qc = useQueryClient();
  const tr = useT();
  const { isMobile } = useDevice();
  const [runMsg, setRunMsg] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('needs');
  // Combinable client-side filters (tier:<t> and src:<source>).
  const [filters, setFilters] = useState<Set<string>>(new Set());
  const toggleFilter = (k: string) => setFilters((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const stats = useQuery<{ stats: Stats }>({ queryKey: ['analysis', 'stats'], queryFn: () => api('/api/analysis/stats') });
  const pending = useQuery<{ analyses: Analysis[] }>({
    queryKey: ['analysis', status],
    queryFn: () => api(`/api/analysis?filter=${STATUS_PARAM[status]}`),
  });

  const [comments, setComments] = useState<Record<string, string>>({});
  const verdict = useMutation({
    mutationFn: ({ id, v, comment }: { id: string; v: boolean; comment?: string }) =>
      api(`/api/analysis/${id}/verdict`, { method: 'POST', body: JSON.stringify({ verdict: v, comment: comment || null }) }),
    onSuccess: (_r, vars) => {
      setComments((c) => { const n = { ...c }; delete n[vars.id]; return n; });
      qc.invalidateQueries({ queryKey: ['analysis'] });
    },
  });

  // Bulk-confirm the twin's stance for every pending public/colleague outcome.
  const confirmShared = useMutation({
    mutationFn: () => api<{ confirmed: number }>('/api/analysis/confirm-shared', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['analysis'] }),
  });
  // Recategorize every entry: conversation tier where linked, else LLM content
  // classification. Categorizes the whole existing backlog in one pass.
  const recategorize = useMutation({
    mutationFn: () => api<{ updated: number; fromConvo: number; fromAI: number; total: number; aiAvailable: boolean }>('/api/analysis/recategorize', { method: 'POST' }),
    onSuccess: (r) => {
      setRunMsg(r.aiAvailable
        ? `Recatégorisé : ${r.updated} mis à jour sur ${r.total} (${r.fromAI} par IA, ${r.fromConvo} par tier de conversation).`
        : `Mode MCP / pas de clé API — seules les stances liées à une conversation peuvent être recatégorisées (${r.updated} mises à jour). Passe en mode API pour la classification IA du contenu.`);
      qc.invalidateQueries({ queryKey: ['analysis'] });
    },
  });

  // Open a stance like a vault card — full detail + linked memory + actions.
  const [detail, setDetail] = useState<Analysis | null>(null);
  const removeStance = useMutation({
    mutationFn: (id: string) => api(`/api/analysis/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setDetail(null); qc.invalidateQueries({ queryKey: ['analysis'] }); },
  });

  const rows = pending.data?.analyses ?? [];
  // Counts for the chips (computed over the status-filtered rows, before tier/source).
  const tierCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const a of rows) if (a.visibility) c[a.visibility] = (c[a.visibility] ?? 0) + 1;
    return c;
  }, [rows]);
  const sourceCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const a of rows) c[a.source] = (c[a.source] ?? 0) + 1;
    return c;
  }, [rows]);

  // Combinable client-side filter: tiers OR'd, sources OR'd, groups AND'd.
  const analyses = useMemo(() => {
    const selTiers = TIERS.filter((t) => filters.has(`tier:${t}`));
    const selSrc = SOURCES.filter((s) => filters.has(`src:${s}`));
    return rows.filter((a) => {
      if (selTiers.length && !(selTiers as readonly string[]).includes(a.visibility ?? '')) return false;
      if (selSrc.length && !(selSrc as readonly string[]).includes(a.source)) return false;
      return true;
    });
  }, [rows, filters]);

  // Desktop two-pane: keep a valid stance selected (auto-advance after a verdict).
  useEffect(() => {
    if (isMobile) return;
    if (!detail || !analyses.some((x) => x.id === detail.id)) setDetail(analyses[0] ?? null);
  }, [isMobile, analyses, detail]);

  const [running, setRunning] = useState(false);
  async function run(count = 1) {
    setRunning(true);
    setRunMsg(count > 1 ? `Recherche de ${count} nouveaux sujets…` : 'Le clone forme une stance…');
    try {
      const r = await api<{ ok: boolean; generated: number; stopped: string | null }>(
        '/api/analysis/run', { method: 'POST', body: JSON.stringify({ language: 'fr', count }) },
      );
      qc.invalidateQueries({ queryKey: ['analysis'] });
      const why = r.stopped === 'daily_cap_reached' ? ' — plafond quotidien atteint'
        : r.stopped === 'no_news' ? ' — aucun nouveau sujet (le fil est à jour ; ajoute des flux RSS pour plus de variété)'
        : '';
      setRunMsg(r.generated > 0
        ? `${r.generated} nouveau${r.generated > 1 ? 'x' : ''} sujet${r.generated > 1 ? 's' : ''} ajouté${r.generated > 1 ? 's' : ''}${why}.`
        : `Rien de nouveau à aligner${why}.`);
    } catch {
      setRunMsg('Mode MCP / pas de clé API — dans Claude Desktop : « prends le prochain sujet d’actualité, cherche dans mes souvenirs, et appelle record_analysis ».');
    } finally {
      setRunning(false);
    }
  }

  const s = stats.data?.stats;
  const rate = s?.alignmentRate;

  const statusChips: Chip[] = [
    { key: 'st:needs', label: tr('align.modeNeeds', { n: s?.threshold ?? 80 }), count: s?.needsAlignment,
      activeCls: 'text-rose-300 border-rose-300/50 bg-rose-300/[0.06]', idleCls: 'border-rose-300/25 text-rose-300/70 hover:text-rose-300' },
    { key: 'st:pending', label: 'en attente', count: s?.pending },
    { key: 'st:verified', label: 'confirmées', count: s?.verified,
      activeCls: 'text-emerald-300 border-emerald-300/50 bg-emerald-300/[0.06]', idleCls: 'border-emerald-300/25 text-emerald-300/70 hover:text-emerald-300' },
    { key: 'st:all', label: 'toutes' },
  ];
  const statusActive = new Set([`st:${status}`]);
  const tierChips: Chip[] = TIERS.map((t) => ({
    key: `tier:${t}`, label: t.replace('_', ' '), count: tierCounts[t] ?? 0,
    activeCls: `${TIER_COLOR[t]} bg-white/[0.05]`, disabled: !tierCounts[t],
  }));
  const sourceChips: Chip[] = SOURCES.filter((sc) => sourceCounts[sc]).map((sc) => ({
    key: `src:${sc}`, label: SOURCE_LABEL[sc] ?? sc, count: sourceCounts[sc] ?? 0,
    activeCls: 'text-particle border-particle/50 bg-white/[0.05]', idleCls: 'border-particle/30 text-particle/70 hover:text-particle',
  }));

  return (
    <div className="px-5 md:px-10 pb-12 max-w-5xl mx-auto space-y-8">
      <header className="pt-2">
        <h1 className="font-serif text-4xl text-ink">{tr('align.title')}<em className="italic text-particle">{tr('align.titleEm')}</em></h1>
        <p className="font-serif italic text-ink/55 mt-2 max-w-2xl">
          Des <b>prédictions</b> du twin sur ce que tu penses — pas des souvenirs. Confirme quand il a vu juste, corrige sinon. Chaque verdict l’entraîne.
        </p>
      </header>

      {/* Scoreboard */}
      <div className="grid grid-cols-4 gap-4">
        <Stat n={rate == null ? '—' : `${rate}%`} label={tr('align.rate')} accent />
        <Stat n={s?.needsAlignment ?? 0} label={tr('align.needYou', { n: s?.threshold ?? 80 })} alert={(s?.needsAlignment ?? 0) > 0} />
        <Stat n={s?.verified ?? 0} label={tr('align.confirmed')} />
        <Stat n={s?.confidentAccuracy == null ? '—' : `${s.confidentAccuracy}%`} label={tr('align.confidentAcc')} />
      </div>

      {/* Forced-alignment banner */}
      {(s?.needsAlignment ?? 0) > 0 && (
        <div className="flex items-center justify-between gap-4 py-3 px-4 rounded-xl border border-rose-300/30 bg-rose-300/[0.05]">
          <p className="font-serif text-[14px] text-ink/80">
            <span className="text-rose-300 font-medium">{s!.needsAlignment}</span> répons{s!.needsAlignment > 1 ? 'es' : 'e'} sous le seuil de {s?.threshold ?? 80}% —
            le twin n’était pas sûr. Aligne-les pour qu’il apprenne.
          </p>
          {status !== 'needs' && (
            <button onClick={() => setStatus('needs')}
              className="px-3 py-1.5 rounded-full bg-rose-300/90 text-void text-[11.5px] hover:bg-rose-300 flex-shrink-0">
              Voir
            </button>
          )}
        </div>
      )}

      {/* Shared-tier bulk confirm — public/colleague stances can be accepted in
          one click instead of answering each manually. */}
      {(s?.sharedPending ?? 0) > 0 && (
        <div className="flex items-center justify-between gap-4 py-3 px-4 rounded-xl border border-sky-300/30 bg-sky-300/[0.05]">
          <p className="font-serif text-[14px] text-ink/80">
            <span className="text-sky-300 font-medium">{s!.sharedPending}</span> stance{s!.sharedPending > 1 ? 's' : ''} <b>public/colleagues</b> —
            tu peux accepter la prédiction du twin d’un coup (les sujets partagés, moins sensibles).
          </p>
          <button onClick={() => confirmShared.mutate()} disabled={confirmShared.isPending}
            className="px-3.5 py-1.5 rounded-full bg-sky-300/90 text-void text-[11.5px] hover:bg-sky-300 flex-shrink-0 disabled:opacity-40">
            {confirmShared.isPending ? '…' : `Confirmer ${s!.sharedPending}`}
          </button>
        </div>
      )}

      {/* Combinable filters (Coffre-style): status (single) + tier + source. */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <FilterChips
            groups={[statusChips, tierChips, sourceChips]}
            active={new Set([...statusActive, ...filters])}
            onToggle={(k) => (k.startsWith('st:') ? setStatus(k.slice(3) as Status) : toggleFilter(k))}
            onClear={() => { setStatus('needs'); setFilters(new Set()); }}
            allLabel="réinitialiser"
            className="flex-1"
          />
          <button onClick={() => recategorize.mutate()} disabled={recategorize.isPending}
            title="Re-dériver le tier de chaque stance depuis sa conversation source"
            className="ml-auto px-3 py-1.5 rounded-full border border-ink/15 text-[11.5px] text-ink/45 hover:text-ink disabled:opacity-40 shrink-0">
            {recategorize.isPending ? '…' : '↻ recategorize'}
          </button>
        </div>
      </div>

      {/* Budget + run */}
      <div className="flex items-center justify-between gap-4 py-3 px-4 rounded-xl border border-ink/10">
        <div className="text-[12px] text-ink/50">
          {tr('align.today', { q: s?.budget.usage.questions ?? 0, max: s?.budget.caps.questionCap ?? '—' })}
          {s?.budget.exhausted && <span className="text-amber"> · {tr('align.capReached')}</span>}
        </div>
        {!s?.budget.exhausted && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => run(5)} disabled={running}
              className="px-4 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
              {running ? '…' : 'Trouver de nouveaux sujets'}
            </button>
            <button onClick={() => run(1)} disabled={running}
              className="px-3 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink disabled:opacity-30">
              +1
            </button>
          </div>
        )}
      </div>
      {runMsg && <p className="font-serif italic text-[13px] text-ink/55 -mt-4">{runMsg}</p>}

      {/* Pending stances */}
      {pending.isLoading ? (
        <div className="text-ink/40 text-[12px]">Loading…</div>
      ) : pending.isError ? (
        <div className="text-center py-10 space-y-3">
          <p className="font-serif italic text-ink/50">Couldn’t load.</p>
          <button onClick={() => pending.refetch()} className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink">Retry</button>
        </div>
      ) : analyses.length === 0 ? (
        <div className="text-center py-16 font-serif italic text-ink/40 space-y-1">
          {filters.size > 0
            ? <p>Aucune stance ne correspond à ces filtres.</p>
            : status === 'needs'
              ? <><p className="text-emerald-300/80">✓ Aligné pour aujourd’hui.</p><p className="text-[13px]">Le twin a appris ce qu’il pouvait — reviens quand il aura formé de nouvelles stances.</p></>
              : <p>{tr('align.noPending')}</p>}
        </div>
      ) : isMobile ? (
        <ul className="space-y-4">
          {analyses.map((a) => (
            <li key={a.id} className="border border-ink/10 rounded-xl p-5 space-y-3">
              {a.context && (
                <div className="flex items-baseline gap-2.5 flex-wrap">
                  <span className="text-[11px] text-amber/70 border border-amber/25 rounded-full px-2 py-0.5 shrink-0">{a.source}</span>
                  <span className="font-serif text-[18px] text-ink/90 leading-snug">{a.context}</span>
                </div>
              )}
              <p onClick={() => setDetail(a)} title="Ouvrir la carte (détail complet)"
                className="font-serif text-xl text-ink leading-snug cursor-pointer hover:text-particle transition-colors">{a.statement}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`px-2.5 py-1 rounded-full text-[11.5px] border ${a.predicted ? 'text-emerald-300 border-emerald-300/30' : 'text-rose-300 border-rose-300/30'}`}>
                  {tr('align.predicts', { v: a.predicted ? tr('common.yes') : tr('common.no') })}
                </span>
                {a.confidence != null && <span className="font-mono text-[10px] text-ink/40">{tr('align.confident', { n: a.confidence })}</span>}
                {(a.confidence == null || a.confidence < (s?.threshold ?? 80)) && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] border border-rose-300/40 text-rose-300">{tr('align.toAlign')}</span>
                )}
                {a.source === 'conversation' && (
                  <span className="text-[11px] text-ink/35">{tr('align.fromConversation')}</span>
                )}
                {a.visibility && TIER_META[a.visibility] && (
                  <span className={`px-2 py-0.5 rounded-full text-[11px] border ${TIER_META[a.visibility]!.cls}`}>
                    {TIER_META[a.visibility]!.label}
                  </span>
                )}
                {a.mlVisibility && a.mlVisibility.tier !== a.visibility && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] border border-particle/40 text-particle/80"
                    title="What the ML categorizer predicts for this stance">
                    ML: {TIER_META[a.mlVisibility.tier]?.label ?? a.mlVisibility.tier} {Math.round(a.mlVisibility.confidence * 100)}%
                  </span>
                )}
              </div>
              {a.reasoning && <p className="font-serif italic text-[14px] text-ink/60 leading-relaxed">“{a.reasoning}”</p>}
              {a.comment && <p className="font-serif text-[13px] text-ink/50">— {a.comment}</p>}

              {/* Optional nuance — folded into the learning signal so a "yes, but…"
                  teaches the twin more than a bare bit. */}
              <textarea
                value={comments[a.id] ?? ''}
                onChange={(e) => setComments((c) => ({ ...c, [a.id]: e.target.value }))}
                placeholder={tr('align.nuance')}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-ink/10 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none resize-none"
              />

              <div className="pt-1">
                <VerdictControl a={a} busy={verdict.isPending}
                  onVerdict={(v) => verdict.mutate({ id: a.id, v, comment: comments[a.id] })} />
                {comments[a.id]?.trim() && (
                  <span className="text-[11px] text-amber/70 mt-1 inline-block">nuance attachée</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        /* Desktop two-pane workbench: list (left) + detail/verdict (right). */
        <div className="grid grid-cols-[minmax(260px,340px)_1fr] gap-5 items-start">
          <ul className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
            {analyses.map((a) => {
              const on = detail?.id === a.id;
              const low = a.confidence == null || a.confidence < (s?.threshold ?? 80);
              return (
                <li key={a.id}>
                  <button onClick={() => setDetail(a)}
                    className={`w-full text-left rounded-xl px-3 py-2.5 border transition-colors ${on ? 'border-amber/40 bg-amber/[0.08]' : 'border-transparent hover:bg-white/[0.03]'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.predicted ? 'bg-emerald-300' : 'bg-rose-300'}`} />
                      {low && <span className="text-[10px] text-rose-300/80">{tr('align.toAlign')}</span>}
                      {a.visibility && TIER_META[a.visibility] && (
                        <span className="text-[10px] text-ink/35 ml-auto">{TIER_META[a.visibility]!.label}</span>
                      )}
                    </div>
                    <div className="font-serif text-[14px] text-ink/85 leading-snug line-clamp-2">{a.statement}</div>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="sticky top-6">
            {detail
              ? <StanceDetail a={detail} deleting={removeStance.isPending}
                  onVerdict={(v, comment) => { verdict.mutate({ id: detail.id, v, comment }); }}
                  onDelete={() => removeStance.mutate(detail.id)} />
              : <div className="text-center py-16 font-serif italic text-ink/40">Sélectionne une stance à gauche.</div>}
          </div>
        </div>
      )}

      {isMobile && detail && (
        <AnalysisDetailModal a={detail} deleting={removeStance.isPending}
          onClose={() => setDetail(null)}
          onVerdict={(v, comment) => { verdict.mutate({ id: detail.id, v, comment }); setDetail(null); }}
          onDelete={() => removeStance.mutate(detail.id)} />
      )}
    </div>
  );
}

// The verdict control. When the twin made a prediction, judging it with the
// Coffre's 👍/👎 is one tap ("did it guess right?"): 👍 = agree with its guess
// (verdict === predicted), 👎 = it was wrong (verdict = !predicted). When there
// is no prediction (e.g. a no-API memory stance), fall back to a direct yes/no.
function VerdictControl({ a, onVerdict, busy }: { a: Analysis; onVerdict: (v: boolean) => void; busy?: boolean }) {
  if (a.predicted == null) {
    return (
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-ink/40 mr-1">Ta réponse :</span>
        <button onClick={() => onVerdict(true)} disabled={busy}
          className="px-4 py-1.5 rounded-full border border-emerald-300/40 text-emerald-300 text-[11.5px] hover:bg-emerald-300/10 disabled:opacity-40">Oui</button>
        <button onClick={() => onVerdict(false)} disabled={busy}
          className="px-4 py-1.5 rounded-full border border-rose-300/40 text-rose-300 text-[11.5px] hover:bg-rose-300/10 disabled:opacity-40">Non</button>
      </div>
    );
  }
  const p = a.predicted;
  return (
    <div className="flex items-center gap-3">
      <span className="text-[11px] text-ink/40 mr-1">Le twin a-t-il vu juste ?</span>
      <ThumbButtons stop={false} downColor="rose" busyUp={busy} busyDown={busy}
        onUp={() => onVerdict(p)} onDown={() => onVerdict(!p)}
        upTitle={`👍 Oui — il a bien deviné (${p ? 'oui' : 'non'})`}
        downTitle={`👎 Non — il s’est trompé (ta réponse : ${p ? 'non' : 'oui'})`} />
    </div>
  );
}

// Detail + verdict for one stance. Used inline as the right pane (desktop) and
// inside a modal (mobile). Mirrors the Vault card: full text, linked memory,
// verdict actions, delete.
function StanceDetail({ a, onVerdict, onDelete, deleting, onClose }: {
  a: Analysis;
  onVerdict: (v: boolean, comment?: string) => void;
  onDelete: () => void; deleting: boolean; onClose?: () => void;
}) {
  const tr = useT();
  const [note, setNote] = useState('');
  const [discuss, setDiscuss] = useState(false);
  useEffect(() => { setNote(''); setDiscuss(false); }, [a.id]); // reset when switching stance
  const memQ = useQuery<{ memory: { id: string; title: string | null; body: string; visibility: string } }>({
    queryKey: ['memory', a.memoryId],
    queryFn: () => api(`/api/memory/${a.memoryId}`),
    enabled: Boolean(a.memoryId),
  });
  const mem = memQ.data?.memory;
  return (
    <div className="bg-void border border-ink/12 rounded-2xl p-5 md:p-6 space-y-4 max-h-[78vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-ink">Stance — détail</h3>
          {onClose && <button onClick={onClose} className="w-8 h-8 rounded-full border border-ink/15 grid place-items-center text-ink/50 hover:text-ink">✕</button>}
        </div>

        {a.context && (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[11px] text-amber/70 border border-amber/25 rounded-full px-2 py-0.5 shrink-0">{a.source}</span>
            <span className="font-serif text-[15px] text-ink/80">{a.context}</span>
          </div>
        )}
        <p className="font-serif text-2xl text-ink leading-snug">{a.statement}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2.5 py-1 rounded-full text-[11.5px] border ${a.predicted ? 'text-emerald-300 border-emerald-300/30' : 'text-rose-300 border-rose-300/30'}`}>
            le twin prédit : {a.predicted == null ? '—' : a.predicted ? 'oui' : 'non'}
          </span>
          {a.confidence != null && <span className="font-mono text-[10px] text-ink/40">{tr('align.confident', { n: a.confidence })}</span>}
          {a.visibility && TIER_META[a.visibility] && (
            <span className={`px-2 py-0.5 rounded-full text-[11px] border ${TIER_META[a.visibility]!.cls}`}>{TIER_META[a.visibility]!.label}</span>
          )}
        </div>
        {a.reasoning && <p className="font-serif italic text-[14px] text-ink/60 leading-relaxed">“{a.reasoning}”</p>}
        {a.comment && <p className="font-serif text-[13px] text-ink/50">— {a.comment}</p>}

        {/* Linked vault memory — the stance is a view over this item. */}
        {a.memoryId && (
          <div className="rounded-xl border border-particle/20 bg-particle/[0.03] p-3 space-y-1">
            <div className="text-[11px] text-particle/80">souvenir lié (Vault)</div>
            {mem ? (
              <>
                {mem.title && <div className="font-serif text-[14px] text-ink/85">{mem.title}</div>}
                <p className="font-serif text-[13px] text-ink/65 leading-relaxed whitespace-pre-wrap">{mem.body}</p>
                <p className="text-[10px] text-ink/35">modifie-le dans le Vault — la stance reflète ce souvenir</p>
              </>
            ) : <div className="font-mono text-[10px] text-ink/40">chargement…</div>}
          </div>
        )}

        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
          placeholder="Ajoute une nuance (optionnel) — prise en compte par l’apprentissage."
          className="w-full px-3 py-2 rounded-lg border border-ink/10 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none resize-none" />

        <div className="flex items-center gap-3 pt-2 border-t border-ink/10">
          <VerdictControl a={a} busy={deleting} onVerdict={(v) => onVerdict(v, note)} />
          <button onClick={() => setDiscuss(true)}
            className="ml-auto px-3 py-1.5 rounded-full border border-ink/15 text-[11.5px] text-ink/55 hover:text-ink">
            Discuter →
          </button>
          <button onClick={() => { if (confirm('Supprimer cette stance (ex. doublon) ?')) onDelete(); }} disabled={deleting}
            className="px-4 py-1.5 rounded-full border border-red-300/30 text-[11.5px] text-red-300/70 hover:text-red-300 disabled:opacity-40">
            Supprimer
          </button>
        </div>
        {discuss && <DiscussStanceModal analysis={a} onClose={() => setDiscuss(false)} />}
    </div>
  );
}

// Mobile wrapper: the stance detail inside a centered modal.
function AnalysisDetailModal({ a, onClose, onVerdict, onDelete, deleting }: {
  a: Analysis; onClose: () => void;
  onVerdict: (v: boolean, comment?: string) => void;
  onDelete: () => void; deleting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[110] bg-black/65 backdrop-blur-md grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-[min(700px,94vw)]">
        <StanceDetail a={a} onVerdict={onVerdict} onDelete={onDelete} deleting={deleting} onClose={onClose} />
      </div>
    </div>
  );
}

function Stat({ n, label, accent, alert }: { n: string | number; label: string; accent?: boolean; alert?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${alert ? 'border-rose-300/30 bg-rose-300/[0.04]' : 'border-ink/10'}`}>
      <div className={`font-serif text-3xl ${alert ? 'text-rose-300' : accent ? 'text-amber' : 'text-ink'}`}>{n}</div>
      <div className="text-[11px] text-ink/40 mt-1">{label}</div>
    </div>
  );
}
